import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  mergeRowDrafts,
  quoteResponseSubmission,
  rowsMissingPrice,
  rowsMissingQuantity,
  seedQuoteResponseDraft,
  submissionErrorKey,
  withPatchedRow,
  type QuoteResponseDraft,
  type RowDraft,
} from '~/pages/quote-response/useQuoteResponseDraft';
import {
  sellerResponseSeed,
  shipFromFacilitiesOf,
} from '~/pages/rfq-view/sellerResponseSeed';
import { useSaveQuoteResponse } from '~/pages/rfq-view/useSaveQuoteResponse';
import {
  buyerQuoteActions,
  type QuoteNegotiationContext,
} from './quoteNegotiation';
import type { Quote } from './splitAwardGridTypes';

export type SellerResponse = Readonly<{
  draft: QuoteResponseDraft;
  isSubmitting: boolean;
  rowIdsMissingQuantity: ReadonlySet<string>;
  rowIdsMissingPrice: ReadonlySet<string>;
  patch: (changes: Partial<Omit<QuoteResponseDraft, 'rows'>>) => void;
  patchRow: (
    platformProductLengthId: string,
    changes: Partial<RowDraft>
  ) => void;
  submit: () => void;
}>;

const NO_ROW_IDS: ReadonlySet<string> = new Set();

/**
 * The answers the buyer types into the comparison grid for sellers who have not quoted
 * yet. `responseOf` gives a quote's answer, or nothing when that seller is answered in
 * the Respond for seller form instead.
 *
 * @example
 *   const sellerResponses = useSellerResponseDrafts(negotiationOf);
 *   const response = sellerResponses.responseOf(quote);
 *   response?.patch({ leadTimeDays: '7' });
 */
export function useSellerResponseDrafts(
  negotiationOf: (quote: Quote) => QuoteNegotiationContext
) {
  const { t } = useTranslation('rfq-view');
  const [drafts, setDrafts] = useState<ReadonlyMap<string, QuoteResponseDraft>>(
    () => new Map()
  );
  const [submittingQuoteIds, setSubmittingQuoteIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [failedSubmitQuoteIds, setFailedSubmitQuoteIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const { save } = useSaveQuoteResponse();
  const seededDrafts = useRef(
    new WeakMap<
      Quote,
      { negotiation: QuoteNegotiationContext; seeded: QuoteResponseDraft }
    >()
  );
  const responses = new Map<string, SellerResponse | undefined>();

  const seededDraftOf = (quote: Quote) => {
    const negotiation = negotiationOf(quote);
    const cached = seededDrafts.current.get(quote);
    if (cached && cached.negotiation === negotiation) return cached.seeded;
    const seeded = seedQuoteResponseDraft(
      sellerResponseSeed(
        quote,
        negotiation.groups,
        negotiation.requestForQuoteCurrency
      ),
      shipFromFacilitiesOf(quote)
    );
    seededDrafts.current.set(quote, { negotiation, seeded });
    return seeded;
  };

  const currentDraftOf = (
    quote: Quote,
    typed: QuoteResponseDraft | undefined
  ) => {
    const seeded = seededDraftOf(quote);
    return typed ? mergeRowDrafts(typed, seeded) : seeded;
  };

  const update = (
    quote: Quote,
    change: (draft: QuoteResponseDraft) => QuoteResponseDraft
  ) =>
    setDrafts((current) =>
      new Map(current).set(
        quote.id,
        change(currentDraftOf(quote, current.get(quote.id)))
      )
    );

  const submit = async (quote: Quote, draft: QuoteResponseDraft) => {
    const { usesWholeRequestPrice } = negotiationOf(quote);
    const errorKey = submissionErrorKey(draft, usesWholeRequestPrice);
    if (errorKey) {
      setFailedSubmitQuoteIds((current) => new Set(current).add(quote.id));
      toast.error(t(errorKey));
      return;
    }
    setSubmittingQuoteIds((current) => new Set(current).add(quote.id));
    try {
      await save(
        quote.id,
        usesWholeRequestPrice,
        quoteResponseSubmission(draft, usesWholeRequestPrice, true)
      );
    } finally {
      setSubmittingQuoteIds((current) => {
        const remaining = new Set(current);
        remaining.delete(quote.id);
        return remaining;
      });
    }
  };

  const buildResponse = (quote: Quote): SellerResponse | undefined => {
    const { hasCannedTally, usesWholeRequestPrice } = negotiationOf(quote);
    if (!buyerQuoteActions(quote).awaitingSeller || hasCannedTally) {
      return undefined;
    }
    const draft = currentDraftOf(quote, drafts.get(quote.id));
    const hasFailedSubmit = failedSubmitQuoteIds.has(quote.id);
    return {
      draft,
      isSubmitting: submittingQuoteIds.has(quote.id),
      rowIdsMissingQuantity: hasFailedSubmit
        ? rowsMissingQuantity(draft, usesWholeRequestPrice)
        : NO_ROW_IDS,
      rowIdsMissingPrice: hasFailedSubmit
        ? rowsMissingPrice(draft, usesWholeRequestPrice)
        : NO_ROW_IDS,
      patch: (changes) =>
        update(quote, (current) => ({ ...current, ...changes })),
      patchRow: (platformProductLengthId, changes) =>
        update(quote, (current) =>
          withPatchedRow(current, platformProductLengthId, changes)
        ),
      submit: () => void submit(quote, draft),
    };
  };

  return {
    responseOf: (quote: Quote) => {
      if (!responses.has(quote.id))
        responses.set(quote.id, buildResponse(quote));
      return responses.get(quote.id);
    },
  };
}

export type SellerResponseDrafts = ReturnType<typeof useSellerResponseDrafts>;
